import React from 'react';
import { LucideIcon } from 'lucide-react';

/**
 * MetricCard Component
 * 
 * Consistent card design based on Performance Analytics page
 * Used across all platform admin pages for visual consistency
 * 
 * Color Palette:
 * - Green (success): Positive metrics, healthy status
 * - Blue (info): Neutral metrics, informational
 * - Yellow (warning): Needs attention, moderate issues
 * - Red (error): Critical issues, failures
 */

export type MetricStatus = 'success' | 'info' | 'warning' | 'error';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon?: LucideIcon;
  status?: MetricStatus;
  change?: number;
  changeLabel?: string;
  description?: string;
  unit?: string;
  statusLabel?: string;
  onClick?: () => void;
}

const statusStyles: Record<MetricStatus, { bg: string; border: string; text: string; badge: string }> = {
  success: {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    text: 'text-green-400',
    badge: 'bg-green-500/20 text-green-400 border-green-500/30'
  },
  info: {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    text: 'text-blue-400',
    badge: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  },
  warning: {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    text: 'text-yellow-400',
    badge: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
  },
  error: {
    bg: 'bg-gray-800',
    border: 'border-gray-700',
    text: 'text-red-400',
    badge: 'bg-red-500/20 text-red-400 border-red-500/30'
  }
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon: Icon,
  status = 'info',
  change,
  changeLabel = 'vs daily',
  description,
  unit = '',
  statusLabel,
  onClick
}) => {
  const styles = statusStyles[status];
  const isPositiveChange = change !== undefined && change > 0;
  const changeColor = isPositiveChange ? 'text-green-400' : change !== undefined && change < 0 ? 'text-red-400' : 'text-gray-400';

  return (
    <div
      className={`${styles.bg} border ${styles.border} rounded-lg p-6 transition-all duration-200 hover:shadow-lg ${
        onClick ? 'cursor-pointer hover:border-gray-600' : ''
      }`}
      onClick={onClick}
    >
      {/* Header with Icon */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {Icon && <Icon className={`h-5 w-5 ${styles.text}`} />}
            <h3 className="text-sm font-medium text-gray-400">{title}</h3>
          </div>
        </div>
      </div>

      {/* Value */}
      <div className="mb-3">
        <div className="flex items-baseline gap-2">
          <p className={`text-3xl font-semibold ${styles.text}`}>
            {value}{unit}
          </p>
          {change !== undefined && (
            <p className={`text-sm font-medium ${changeColor}`}>
              {change > 0 ? '+' : ''}{change}%
            </p>
          )}
        </div>
        {changeLabel && change !== undefined && (
          <p className="text-xs text-gray-500 mt-1">
            {changeLabel}
          </p>
        )}
      </div>

      {/* Description */}
      {description && (
        <p className="text-sm text-gray-400 mb-3">
          {description}
        </p>
      )}

      {/* Status Badge */}
      {statusLabel && (
        <div className="mt-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${styles.badge}`}>
            {statusLabel}
          </span>
        </div>
      )}
    </div>
  );
};

export default MetricCard;
